import {
    buildPath,
    buildTemplatePath,
    CrudMode,
    Dialog, 
    handleClose,
    Page,
    PageRoute,
    pn,
    ScreenType,
    Stack,
    useParams,
    useUnsavedChanges,
    useValidationErrors,
} from "@compass/core";
import {
    Permission,
    DiplomaType,
    DiplomaTypeEditResource,
    DiplomaTypeViewResource,
    Topic,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { TopicPageHeader } from "./TopicPageHeader";
import { TopicPageBody } from "./TopicPageBody";

const routeName = "topic";

class Params {
    id: number = 0;
    crudMode: CrudMode = "view";
    screenType: ScreenType = "standard";
}

export const topicPageRoute: PageRoute<Params> = {
    handle: "Topic",
    templatePath: buildTemplatePath(routeName, Params),
    getPath: (params) => buildPath(routeName, params),
};

export const TopicPage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;
    const navigate = useNavigate();
    const { params } = useParams(routeName, Params);
    const [topic, setTopic] = useState<Topic>({} as Topic);
    const [initTopic, setInitTopic] = useState<Topic>(topic);
    const [viewResource, setViewResource] = useState<DiplomaTypeViewResource>({});
    const [editResource, setEditResource] = useState<DiplomaTypeEditResource>({});
    const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [topic] });
    const { validationErrors, setValidationErrors } = useValidationErrors();

    useEffect(() => {
        fetch(params.id);

        ApiService.topics.getViewResource().then((response) => {
            setViewResource(response);
        });
    }, []);

    useEffect(() => {
        if (params.crudMode === "edit") {
            ApiService.topics.getEditResource().then((response) => {
                setEditResource(response);
            });
        }
    }, [params.crudMode]);

    const reroute = (id: number, crudMode: CrudMode) =>
        navigate(`../${topicPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType, })}`);

    const fetch = (id: number) => {
        ApiService.topics.get(id).then((response) => {
            setTopic(response);
            setInitTopic(response);
            resetUnsavedChanges([response]);
        });
    };

    const pageHeader = (
        <TopicPageHeader
            rootPermission={ROOT_PERMISSION}
            handle={topicPageRoute.handle}
            crudMode={params.crudMode}
            reroute={reroute}
            fetch={fetch}
            topic={topic}
            setTopic={setTopic}
            initTopic={initTopic}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
            resetUnsavedChanges={resetUnsavedChanges}
        />
    );

    const pageBody = (
        <Stack spacing="lg">
            <TopicPageBody
                crudMode={params.crudMode}
                topic={topic}
                setTopic={setTopic}
                viewResource={viewResource}
                editResource={editResource}
                validationErrors={validationErrors}
            />
        </Stack>
    );

    return (
        <Dialog
            isForm
            open={true}
            onClose={() => handleClose(navigate, unsavedChanges)}
            maxWidth="md"
            header={pageHeader}
            content={pageBody}
        />
    );
};
